import { useEffect, useRef } from 'react';
import type { FieldPath, FieldValues, UseFormReturn } from 'react-hook-form';

import type { ProviderFieldError } from '@/domain/providerConfig';

/** The error type these rules set, so a field's own rule or a server message can be told apart. */
export const CLIENT_ERROR = 'client';

/**
 * Shows the save rules that span fields (required on a touched pen, ids another pen uses,
 * a credential that would follow a new destination) at the fields they name, and takes
 * each one away once it no longer applies. A field's own rule or a server message at the
 * same path is left in place. `isPath` says which paths the form holds; others are skipped.
 */
export function useClientErrors<T extends FieldValues>(
    form: UseFormReturn<T>,
    errors: readonly ProviderFieldError[],
    isPath: (path: string) => path is FieldPath<T>
): void {
    const applied = useRef<Set<string>>(new Set());
    // Runs after every render: a field's own rule clears its path on each edit, and the
    // rule here may still apply.
    useEffect(() => {
        const next = new Set<string>();
        for (const { path, code } of errors) {
            if (!isPath(path)) continue;
            next.add(path);
            const current = form.getFieldState(path).error;
            if (current === undefined) form.setError(path, { type: CLIENT_ERROR, message: code });
            else if (current.type === CLIENT_ERROR && current.message !== code)
                form.setError(path, { type: CLIENT_ERROR, message: code });
        }
        for (const path of applied.current)
            if (
                !next.has(path) &&
                isPath(path) &&
                form.getFieldState(path).error?.type === CLIENT_ERROR
            )
                form.clearErrors(path);
        applied.current = next;
    });
}
